import React, { useState, useEffect } from 'react';
import { dashboardService } from '../services/api';
import { FaUsers, FaUserFriends, FaBuilding, FaChartLine } from 'react-icons/fa';
import styled from 'styled-components';

const DashboardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  background: ${props => {
    switch (props.type) {
      case 'clients': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'users': return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      case 'active': return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
      case 'growth': return 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)';
      default: return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
  }};
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #7f8c8d;
  font-weight: 500;
`;

const StatChange = styled.div`
  font-size: 12px;
  color: ${props => props.positive ? '#27ae60' : '#e74c3c'};
  font-weight: 500;
`;

const ChartContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
`;

const ChartTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #fcc;
`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.getStats();
        setStats(data);
      } catch (err) {
        setError('Erro ao carregar estatísticas do dashboard');
        console.error('Erro ao carregar stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  // Dados mockados para demonstração (substitua pela API real)
  const mockStats = {
    totalClients: 156,
    totalUsers: 1247,
    activeClients: 142,
    growthRate: 12.5
  };

  const displayStats = stats || mockStats;

  return (
    <div>
      <DashboardContainer>
        <StatCard>
          <StatHeader>
            <div>
              <StatValue>{displayStats.totalClients}</StatValue>
              <StatLabel>Total de Clientes</StatLabel>
            </div>
            <StatIcon type="clients">
              <FaBuilding />
            </StatIcon>
          </StatHeader>
          <StatChange positive={true}>
            +{displayStats.growthRate}% este mês
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <div>
              <StatValue>{displayStats.totalUsers}</StatValue>
              <StatLabel>Total de Usuários</StatLabel>
            </div>
            <StatIcon type="users">
              <FaUsers />
            </StatIcon>
          </StatHeader>
          <StatChange positive={true}>
            +8.2% este mês
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <div>
              <StatValue>{displayStats.activeClients}</StatValue>
              <StatLabel>Clientes Ativos</StatLabel>
            </div>
            <StatIcon type="active">
              <FaUserFriends />
            </StatIcon>
          </StatHeader>
          <StatChange positive={true}>
            {((displayStats.activeClients / displayStats.totalClients) * 100).toFixed(1)}% ativos
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <div>
              <StatValue>{displayStats.growthRate}%</StatValue>
              <StatLabel>Crescimento</StatLabel>
            </div>
            <StatIcon type="growth">
              <FaChartLine />
            </StatIcon>
          </StatHeader>
          <StatChange positive={displayStats.growthRate > 0}>
            {displayStats.growthRate > 0 ? '+' : ''}{displayStats.growthRate}% vs mês anterior
          </StatChange>
        </StatCard>
      </DashboardContainer>

      <ChartContainer>
        <ChartTitle>Resumo de Atividade</ChartTitle>
        <p style={{ color: '#7f8c8d', margin: 0 }}>
          O painel mostra um total de {displayStats.totalClients} clientes, 
          sendo {displayStats.activeClients} ativos, e {displayStats.totalUsers} usuários cadastrados no sistema.
        </p>
      </ChartContainer>
    </div>
  );
};

export default Dashboard; 